import React, { PureComponent } from 'react';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepButton from '@material-ui/core/StepButton';
import StepLabel from '@material-ui/core/StepLabel';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Translator from 'translator';
import { FormBackButton, FHEContainerScroll, FormNextButton } from 'data/components';

class FHEStepper extends PureComponent {
    static propTypes = {
        steps: PropTypes.array.isRequired,
        classes: PropTypes.object.isRequired,
        nonLinear: PropTypes.bool
    };

    static defaultProps = {
        nonLinear: false
    };

    constructor(props) {
        super(props);
        this.state = {
            activeStep: 0,
            completed: new Set(),
            skipped: new Set()
        };
    }

    totalSteps = () => {
        const { steps } = this.props;
        return steps.length;
    };

    isStepOptional = step => {
        const { steps } = this.props;
        return !!steps[step].optional;
    };

    handleSkip = () => {
        const { activeStep } = this.state;
        if (!this.isStepOptional(activeStep)) {
            // You probably want to guard against something like this
            // it should never occur unless someone's actively trying to break something.
            throw new Error("You can't skip a step that isn't optional.");
        }

        this.setState(prevState => {
            const newActiveStep = prevState.activeStep + 1;
            const newSkipped = new Set(prevState.skipped.values());
            newSkipped.add(newActiveStep);
            return {
                activeStep: newActiveStep,
                skipped: newSkipped
            };
        });
    };

    skippedSteps = () => {
        const { skipped } = this.state;
        return skipped.size;
    };

    completedSteps = () => {
        const { completed } = this.state;
        return completed.size;
    };

    allStepsCompleted = () => {
        return this.completedSteps() === this.totalSteps() - this.skippedSteps();
    };

    isLastStep = () => {
        const { activeStep } = this.state;
        return activeStep === this.totalSteps() - 1;
    };

    handleNext = () => {
        const { activeStep, completed } = this.state;
        const { steps } = this.props;
        const newActiveStep =
            this.isLastStep() && !this.allStepsCompleted()
                ? // It's the last step, but not all steps have been completed
                  // find the first step that has been completed
                  steps.findIndex((step, i) => !completed.has(i))
                : activeStep + 1;

        this.setState({
            activeStep: newActiveStep
        });
    };

    handleBack = () => {
        this.setState(prevState => ({ activeStep: prevState.activeStep - 1 }));
    };

    handleStep = step => () => {
        this.setState({ activeStep: step });
    };

    handleComplete = () => {
        const { completed, activeStep } = this.state;
        const newCompleted = new Set(completed);
        newCompleted.add(activeStep);
        this.setState({ completed: newCompleted });

        if (completed.size !== this.totalSteps() - this.skippedSteps()) {
            this.handleNext();
        }
    };

    handleReset = () => {
        this.setState({ activeStep: 0, completed: new Set(), skipped: new Set() });
    };

    isStepSkipped = step => {
        const { skipped } = this.state;
        return skipped.has(step);
    };

    isStepComplete = step => {
        const { completed } = this.state;
        return completed.has(step);
    };

    render() {
        const { activeStep, completed } = this.state;
        const { nonLinear, classes, steps } = this.props;
        let isClickable = true;
        return (
            <div className={classes.root}>
                {!nonLinear && (
                    <div className={classes.btnContainer}>
                        <FormBackButton disabled={activeStep === 0} sendFunc={this.handleBack} className={classes.button} />
                        <FormNextButton disabled={this.isLastStep()} sendFunc={this.handleNext} className={classes.button} />
                    </div>
                )}
                {this.isStepOptional(activeStep) && !completed.has(activeStep) && (
                    <Button variant="contained" color="primary" onClick={this.handleSkip} className={classes.skipBtn}>
                        {Translator.translate('general.skip')}
                    </Button>
                )}
                <Stepper alternativeLabel nonLinear={nonLinear} activeStep={activeStep}>
                    {steps.map((step, index) => {
                        let actualClickable = isClickable;
                        if (
                            isClickable &&
                            ((!this.isStepOptional(index) && !this.isStepComplete(index)) ||
                                (this.isStepOptional(index) && !this.isStepSkipped(index)))
                        )
                            isClickable = false;
                        const stepProps = {};
                        const buttonProps = {};
                        if (this.isStepOptional(index)) {
                            buttonProps.optional = <Typography variant="caption">{Translator.translate('general.optional')}</Typography>;
                        }
                        if (this.isStepSkipped(index)) {
                            stepProps.completed = false;
                        }

                        return (
                            <Step key={step.label} disabled={!actualClickable} {...stepProps}>
                                <StepButton onClick={this.handleStep(index)} completed={this.isStepComplete(index)} {...buttonProps}>
                                    <StepLabel error={step.error}>{step.label}</StepLabel>
                                </StepButton>
                            </Step>
                        );
                    })}
                </Stepper>
                <FHEContainerScroll>{steps[activeStep].component}</FHEContainerScroll>
            </div>
        );
    }
}

const styles = theme => ({
    btnContainer: {
        display: 'flex',
        justifyContent: 'space-around'
    },
    root: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 400
    },
    button: {
        marginRight: theme.spacing(1)
    },
    skipBtn: {
        position: 'absolute',
        top: 50,
        right: 50,
        [theme.breakpoints.down('sm')]: {
            top: 30,
            right: 30
        },
        [theme.breakpoints.down('xs')]: {
            top: 18,
            right: 18
        }
    },
    backButton: {
        marginRight: theme.spacing(1)
    },
    completed: {
        display: 'inline-block'
    },
    instructions: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1)
    }
});

export default withStyles(styles, { withTheme: true })(FHEStepper);



// WEBPACK FOOTER //
// ./data/components/FHEStepper.js